import railyApiService from "../raily-api.service";
import TaxiCorporationAddRequest, {
  TaxiCorporationAddRequestBody,
} from "./add/taxi-corporation-add-request";
import taxiCorporationAddRequestFactory from "./add/taxi-corporation-add-request.factory";
import TaxiCorporationAddResponse from "./add/taxi-corporation-add-response";
import taxiCorporationCargoTaxiContractListRequestFactory from "./cargo-taxi-contract/list/taxi-corporation-cargo-taxi-contract-list-request.factory";
import TaxiCorporationCargoTaxiContractListRequest, {
  TaxiCorporationCargoTaxiContractListRequestQueryParams,
} from "./cargo-taxi-contract/list/taxi-corporation-cargo-taxi-contract-list.request";
import TaxiCorporationCargoTaxiContractListResponse from "./cargo-taxi-contract/list/taxi-corporation-cargo-taxi-contract-list.response";
import TaxiCorporationDetailsRequest from "./details/taxi-corporation-details.request";
import TaxiCorporationDetailsResponse from "./details/taxi-corporation-details.response";
import taxiCorporationListRequestFactory from "./list/taxi-corporation-list-request.factory";
import TaxiCorporationListRequest, {
  TaxiCorporationListRequestQueryParams,
} from "./list/taxi-corporation-list.request";
import TaxiCorporationListResponse from "./list/taxi-corporation-list.response";
import TaxiCorporationPlanEntryBillingsDownloadRequest from "./plan-entry-billings/download/taxi-corporation-plan-entry-billings-download.request";
import taxiCorporationPlanEntryBillingsDownloadRequestFactory from "./plan-entry-billings/download/taxi-corporation-plan-entry-billings-download.request.factory";
import TaxiCorporationPlanEntryBillingsDownloadResponse from "./plan-entry-billings/download/taxi-corporation-plan-entry-billings-download.response";
import TaxiCorporationPlanEntryBillingsRequest from "./plan-entry-billings/list/taxi-corporation-plan-entry-billings.request";
import taxiCorporationPlanEntryBillingsRequestFactory from "./plan-entry-billings/list/taxi-corporation-plan-entry-billings.request.factory";
import TaxiCorporationPlanEntryBillingsResponse from "./plan-entry-billings/list/taxi-corporation-plan-entry-billings.response";
import TaxiCorporationOrderBillingsListRequest, {
  TaxiCorporationOrderBillingsListQueryParams,
} from "./order-billings/list/taxi-corporation-order-billings-list.request";
import TaxiCorporationOrderBillingsListResponse from "./order-billings/list/taxi-corporation-order-billings-list.response";
import taxiCorporationOrderBillingsListRequestFactory from "./order-billings/list/taxi-corporation-order-billings-list.request.factory";
import TaxiCorporationOrderBillingsDownloadRequest, {
  TaxiCorporationOrderBillingsDownloadQueryParams,
} from "./order-billings/download/taxi-corporation-order-billings-download.request";
import TaxiCorporationOrderBillingsDownloadResponse from "./order-billings/download/taxi-corporation-order-billings-download.response";
import taxiCorporationOrderBillingsDownloadRequestFactory from "./order-billings/download/taxi-corporation-order-billings-download-request.factory";
import TaxiCorporationUnbilledOrdersListRequest from "./unbilled/orders-list/taxi-corporation-unbilled-orders-list.request";
import taxiCorporationUnbilledOrdersListRequestFactory from "./unbilled/orders-list/taxi-corporation-unbilled-orders-list.request.factory";
import TaxiCorporationUnbilledOrdersListResponse from "./unbilled/orders-list/taxi-corporation-unbilled-orders-list.response";
import TaxiCorporationUnbilledRouteListRequest from "./unbilled/route-list/taxi-corporation-unbilled-route-list.request";
import taxiCorporationUnbilledRouteListRequestFactory from "./unbilled/route-list/taxi-corporation-unbilled-route-list.request.factory";
import TaxiCorporationUnbilledRouteListResponse from "./unbilled/route-list/taxi-corporation-unbilled-route-list.response";
import TaxiCorporationDriverPlanRequest from "./driver/plan/taxi-corporation-driver-plan.request";
import TaxiCorporationDriverPlanResponse from "./driver/plan/taxi-corporation-driver-plan.response";
import taxiCorporationFleetPartnerAddRequestFactory from "./fleet-partner/add/taxi-corporation-fleet-partner-add-request.factory";
import TaxiCorporationFleetPartnerAddRequest, {
  TaxiCorporationFleetPartnerAddRequestBody,
} from "./fleet-partner/add/taxi-corporation-fleet-partner-add.request";
import TaxiCorporationFleetPartnerListRequest from "./fleet-partner/list/taxi-corporation-fleet-partner-list.request";
import TaxiCorporationFleetPartnerListResponse from "./fleet-partner/list/taxi-corporation-fleet-partner-list.response";
import taxiCorporationFleetPartnerUpdateRequestFactory from "./fleet-partner/update/taxi-corporation-fleet-partner-update-request.factory";
import TaxiCorporationFleetPartnerUpdateRequest, {
  TaxiCorporationFleetPartnerUpdateRequestBody,
} from "./fleet-partner/update/taxi-corporation-fleet-partner-update.request";
import TaxiCorporationFleetPartnerUpdateResponse from "./fleet-partner/update/taxi-corporation-fleet-partner-update.response";

const taxiCorporationApiService = () => {
  const getDetails = async (
    request: TaxiCorporationDetailsRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationDetailsResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}`;

    return railyApiService().get<TaxiCorporationDetailsResponse>({
      url,
      abortSignal,
    });
  };

  const getList = (
    request: TaxiCorporationListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationListResponse> => {
    const url = `/taxi-corporations`;

    const queryParams: TaxiCorporationListRequestQueryParams =
      taxiCorporationListRequestFactory.createQueryParams(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const add = async (
    request: TaxiCorporationAddRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationAddResponse> => {
    const url = `/taxi-corporations`;

    const body: TaxiCorporationAddRequestBody =
      taxiCorporationAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const getCargoTaxiContractList = async (
    request: TaxiCorporationCargoTaxiContractListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationCargoTaxiContractListResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/cargo-taxi/contracts`;

    const queryParams: TaxiCorporationCargoTaxiContractListRequestQueryParams =
      taxiCorporationCargoTaxiContractListRequestFactory.createQueryParams(
        request,
      );

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getPlanEntryBillings = (
    request: TaxiCorporationPlanEntryBillingsRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationPlanEntryBillingsResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/plan-entry-billings`;

    const queryParams =
      taxiCorporationPlanEntryBillingsRequestFactory.createQueryParams(request);
    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const gerOrderBillingsList = (
    request: TaxiCorporationOrderBillingsListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationOrderBillingsListResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/order-billings`;

    const queryParams: TaxiCorporationOrderBillingsListQueryParams =
      taxiCorporationOrderBillingsListRequestFactory.createQueryParams(request);
    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getUnbilledOrdersList = (
    request: TaxiCorporationUnbilledOrdersListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationUnbilledOrdersListResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/orders/not-yet-billed`;

    const queryParams =
      taxiCorporationUnbilledOrdersListRequestFactory.createQueryParams(
        request,
      );
    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getUnbilledRouteList = (
    request: TaxiCorporationUnbilledRouteListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationUnbilledRouteListResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/plan-entries/not-yet-billed`;

    const queryParams =
      taxiCorporationUnbilledRouteListRequestFactory.create(request);

    return railyApiService().get({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getPlanEntryBillingsReport = async (
    request: TaxiCorporationPlanEntryBillingsDownloadRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationPlanEntryBillingsDownloadResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/plan-entry-billings/csv-dump`;
    const queryParams =
      taxiCorporationPlanEntryBillingsDownloadRequestFactory.createQueryParams(
        request,
      );

    return railyApiService().getFile({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getOrderBillingsReport = async (
    request: TaxiCorporationOrderBillingsDownloadRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationOrderBillingsDownloadResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/order-billings/csv-dump`;

    const queryParams: TaxiCorporationOrderBillingsDownloadQueryParams =
      taxiCorporationOrderBillingsDownloadRequestFactory.createQueryParams(
        request,
      );

    return railyApiService().getFile({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getDriverPlan = (
    request: TaxiCorporationDriverPlanRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationDriverPlanResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/drivers/${request.driverUuid}/plan`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const addFleetPartner = async (
    request: TaxiCorporationFleetPartnerAddRequest,
    abortSignal: AbortSignal,
  ) => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/fleet-partners`;

    const body: TaxiCorporationFleetPartnerAddRequestBody =
      taxiCorporationFleetPartnerAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const getFleetPartnerList = async (
    request: TaxiCorporationFleetPartnerListRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationFleetPartnerListResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationUuid}/fleet-partners`;

    return railyApiService().get<TaxiCorporationFleetPartnerListResponse>({
      url,
      abortSignal,
    });
  };

  const updateFleetPartner = async (
    request: TaxiCorporationFleetPartnerUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<TaxiCorporationFleetPartnerUpdateResponse> => {
    const url = `/taxi-corporations/${request.taxiCorporationId}/fleet-partners`;

    const body: TaxiCorporationFleetPartnerUpdateRequestBody =
      taxiCorporationFleetPartnerUpdateRequestFactory.createBody(request);

    return railyApiService().put<TaxiCorporationFleetPartnerUpdateResponse>({
      url,
      abortSignal,
      body,
    });
  };

  return {
    getDetails,
    getList,
    add,
    getCargoTaxiContractList,
    getPlanEntryBillings,
    getPlanEntryBillingsReport,
    gerOrderBillingsList,
    getOrderBillingsReport,
    getUnbilledOrdersList,
    getUnbilledRouteList,
    getDriverPlan,
    addFleetPartner,
    getFleetPartnerList,
    updateFleetPartner,
  };
};

export default taxiCorporationApiService;
